import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Card,
  CardContent,
  CardActions,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import nl from "date-fns/locale/nl";
import Loading from "../common/Loading";
import AppSnackbar from "../../common/AppSnackbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useReservationAdminAPI } from "../../service/useReservationAdminAPI";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { getDateStringToAvoidTimezone } from "../../common/DateUtil";
import { useAppointmentGraphQL } from "../../service/useAppointmentGraphQL";
import { useAppointmentsAPI } from "../../service/useAppointmentsAPI";
import InfoRow from "../common/InfoRow";
import AddAppointmentDialog from "./AddAppointmentDialog";

const AddAppointment = ({ initialData, appointmentId, onSave, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const isEditMode = !!appointmentId || !!initialData;

  const [inputValue, setInputValue] = useState("");
  const { searchByAnimalName, createAppointment, updateAppointment } = useReservationAdminAPI();
  const { getOpenAppointmentDatesForServiceAndDate } = useAppointmentsAPI();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [animals, setAnimals] = useState([]);
  const [currentAnimal, setCurrentAnimal] = useState({
    animals: [],
    person: {},
  });
  const [newAppointmentDto, setNewAppointmentDto] = useState({
    personId: undefined,
    animalsInReservationIds: [],
    startAt: undefined,
    service: {},
  });
  const { useActiveServices } = useAppointmentGraphQL();
  const { data: servicesData, loading: servicesLoading } = useActiveServices();
  const [openEditModal, setOpenEditModal] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("error");

  // Tabs state: step 1 (details) -> step 2 (select stay)
  const [activeTab, setActiveTab] = useState("1");
  const [canGoToStep2, setCanGoToStep2] = useState(false);
  const [selectedAppointments, setSelectedAppointments] = useState([]);
  const [openAppointmentSchedules, setOpenAppointmentSchedules] = useState([]);

  React.useEffect(() => {
    if (isEditMode && initialData) {
      setNewAppointmentDto({
        uuid: initialData.uuid,
        personId: initialData.personId || initialData.person?.id,
        animalsInReservationIds: initialData.animalsInReservationIds || [],
        startAt: initialData.startAt ? getDateStringToAvoidTimezone(new Date(initialData.startAt)) : undefined,
        service: {},
        notes: initialData.notes || "",
      });
      setCurrentAnimal({
        person: initialData.person || {},
        animals: initialData.animals || [],
      });
      setSelectedAppointments(initialData.selectedAppointments || []);
      setCanGoToStep2(true);
      setActiveTab("1");
      setOpenEditModal(true);
    }
  }, [isEditMode, initialData]);

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    if (!isEditMode) {
      setNewAppointmentDto({
        personId: undefined,
        animalsInReservationIds: [],
        startAt: undefined,
        service: {},
      });
      setActiveTab("1");
      setCanGoToStep2(false);
      setSelectedAppointments([]);
    }
    onClose?.();
  };

  const handleAddOption = () => {
    if (!inputValue) return;
    searchByAnimalName(inputValue)
      .then((value) => setAnimals(value))
      .catch(() => handleFetchError(t("searchFailure")));
  };

  const handleSaveAppointment = () => {
    const payload = {
      uuid: newAppointmentDto.uuid,
      personId: newAppointmentDto.personId,
      startAt: newAppointmentDto.startAt,
      notes: newAppointmentDto.notes || undefined,
      animalTimeServiceAppointments: selectedAppointments.map(
        (selectedAppointment) => ({
          animalId: selectedAppointment.animalId,
          serviceUuid: newAppointmentDto?.service?.uuid,
          startAtInstantTime: selectedAppointment.startAt,
        }),
      ),
    };

    const action = isEditMode
      ? updateAppointment(payload)
      : createAppointment(payload);

    action
      .then(() => {
        handleCloseEditModal();
        handleSuccessMessage(
          isEditMode
            ? "updatedReservationSuccessful"
            : "createdReservationSuccessful",
        );
        onSave?.();
      })
      .catch(() =>
        handleFetchError(
          t(isEditMode ? "updateReservationFailed" : "createReservationFailed"),
        ),
      );
  };

  function handleFetchError(reason) {
    setSnackbarMessage(reason);
    setSeverity("error");
    setOpenSnackbar(true);
  }

  function handleSuccessMessage(reason) {
    setSnackbarMessage(reason);
    setSeverity("success");
    setOpenSnackbar(true);
  }

  const handleInputChange = (event) => setInputValue(event.target.value);

  const handleSelectChange = (eventOrValue, field) => {
    let value = eventOrValue?.target ? eventOrValue.target.value : eventOrValue;
    if (field === "startAt") {
      value = value ? getDateStringToAvoidTimezone(value) : undefined;
    }
    setNewAppointmentDto((prev) => ({ ...prev, [field]: value }));
  };

  const toggleAnimalForAppointments = (id) => {
    setNewAppointmentDto((prev) => {
      const exists = prev.animalsInReservationIds.includes(id);
      return {
        ...prev,
        animalsInReservationIds: exists
          ? prev.animalsInReservationIds.filter((x) => x !== id)
          : [...prev.animalsInReservationIds, id],
      };
    });
  };

  function checkAvailability() {
    const { startAt, service } = newAppointmentDto;
    setLoading(true);
    getOpenAppointmentDatesForServiceAndDate(service.uuid, startAt)
      .then((openAppointmentSchedule) => {
        setActiveTab("2");
        setCanGoToStep2(true);
        setOpenAppointmentSchedules(openAppointmentSchedule || []);
      })
      .catch(() => {
        setError(t("timeslotsFetchFailed"));
      })
      .finally(() => setLoading(false));
  }

  const onSelectService = (service) => {
    handleSelectChange(service, "service");
  };

  const toggleAppointment = (openAppointment, animalKey, animal) => {
    // Is this exact timeslot already selected for this row?
    const isSelectedForThisRow = selectedAppointments.find(
      (value) =>
        value.uuid === openAppointment.uuid && value._animalKey === animalKey,
    );

    if (isSelectedForThisRow) {
      // Deselect it for this row
      const remaining = selectedAppointments.filter(
        (value) =>
          !(
            value.uuid === openAppointment.uuid &&
            value._animalKey === animalKey
          ),
      );
      setSelectedAppointments(remaining);
      return;
    }

    // Enforce uniqueness of a timeslot across rows:
    // - Remove the same timeslot from all other rows
    // - Replace any existing selection for this row with the new one
    const withoutSameSlot = selectedAppointments.filter(
      (value) => value.uuid !== openAppointment.uuid,
    );
    const withoutThisRow = withoutSameSlot.filter(
      (value) => value._animalKey !== animalKey,
    );
    const withNew = [
      ...withoutThisRow,
      {
        ...openAppointment,
        _animalKey: animalKey,
        animalId: animal.id,
        animalName: animal.animalName,
      },
    ];
    setSelectedAppointments(withNew);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={nl}>
      <Grid
        container
        justifyContent="center"
        sx={{ px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 2 } }}
      >
        <Grid item xs={12} sm={12} sx={{ p: 2, m: 2 }}>
          <Grid item xs={12}>
            <Typography variant={isXs ? "h6" : "h5"} sx={{ mb: 1 }}>
              {t("addAppointment")}
            </Typography>
          </Grid>

          {/* Search */}
          <Grid item xs={12}>
            <TextField
              fullWidth={isXs}
              required
              value={inputValue}
              onChange={handleInputChange}
              label={t("animalNameLabel")}
              variant="outlined"
              inputProps={{ "data-testid": "search-animal-input" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button onClick={handleAddOption} variant="contained" data-testid="search-animal-button">
                      <SearchIcon />
                    </Button>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleAddOption();
                }
              }}
            />
          </Grid>

          {/* Results: cards on mobile, table on desktop */}
          {isXs ? (
            <Grid item xs={12}>
              <Grid container spacing={1}>
                {animals.map((ci, idx) => (
                  <Grid item xs={12} key={ci?.person?.id || idx}>
                    <Card variant="outlined">
                      <CardContent sx={{ display: "grid", gap: 1 }}>
                        <InfoRow label={t("catName")} value={ci?.name} />
                        <InfoRow
                          label={t("ownerName")}
                          value={`${ci?.person?.forename ?? ""} ${ci?.person?.name ?? ""}`}
                        />
                        <InfoRow
                          label={t("familie")}
                          value={(ci?.animals ?? [])
                            .map((c) => c.name)
                            .join(", ")}
                        />
                      </CardContent>
                      <CardActions sx={{ justifyContent: "flex-end" }}>
                        <Button
                          onClick={() => {
                            setCurrentAnimal(ci);
                            setOpenEditModal(true);
                            setNewAppointmentDto((prev) => ({
                              ...prev,
                              personId: ci?.person?.id,
                            }));
                          }}
                          data-testid={`add-appointment-button-${ci?.name}`}
                        >
                          {t("addReservation")}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <TableContainer component={Paper}>
                <Table size="small" aria-label="search results table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("animalNameLabel")}</TableCell>
                      <TableCell>{t("ownerName")}</TableCell>
                      <TableCell>{t("familie")}</TableCell>
                      <TableCell>{t("actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {animals.map((ci, index) => (
                      <TableRow key={index}>
                        <TableCell>{ci.name}</TableCell>
                        <TableCell>{`${ci.person.forename} ${ci.person.name}`}</TableCell>
                        <TableCell>
                          {(ci.animals || []).map((c) => c.name).join(", ")}
                        </TableCell>
                        <TableCell>
                          <Button
                            onClick={() => {
                              setCurrentAnimal(ci);
                              setOpenEditModal(true);
                              setNewAppointmentDto((prev) => ({
                                ...prev,
                                personId: ci.person.id,
                              }));
                            }}
                            color="primary"
                            data-testid={`add-appointment-button-${ci.name}`}
                          >
                            {t("addAppointment")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}

          <AppSnackbar
            openSnackbar={openSnackbar}
            snackbarMessage={snackbarMessage}
            setOpenSnackbar={setOpenSnackbar}
            severity={severity}
          />

          <AddAppointmentDialog
            filteredServices={servicesData?.services || []}
            open={openEditModal}
            onClose={handleCloseEditModal}
            fullScreen={fullScreen}
            isXs={isXs}
            t={t}
            isEditMode={isEditMode}
            currentAnimal={currentAnimal}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            canGoToStep2={canGoToStep2}
            newAppointmentDto={newAppointmentDto}
            toggleAnimalForAppointments={toggleAnimalForAppointments}
            handleSelectChange={handleSelectChange}
            servicesLoading={servicesLoading}
            onSelectService={onSelectService}
            loading={loading}
            error={error}
            openAppointmentSchedules={openAppointmentSchedules}
            selectedAppointments={selectedAppointments}
            toggleAppointment={toggleAppointment}
            animals={animals}
            checkAvailability={checkAvailability}
            handleSaveAppointment={handleSaveAppointment}
          />

          <AppSnackbar
            openSnackbar={openSnackbar}
            snackbarMessage={snackbarMessage}
            setOpenSnackbar={setOpenSnackbar}
            severity={severity}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
};

export default withAuthenticationRequired(AddAppointment, {
  onRedirecting: () => <Loading />,
  returnTo: "/login",
});
